import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'

import { cn } from '@/lib/utils'

type ButtonVariant = 'primary' | 'secondary' | 'ghost'
type ButtonSize = 'md' | 'lg'

type SharedProps = {
  children: ReactNode
  className?: string
  variant?: ButtonVariant
  size?: ButtonSize
}

type LinkButtonProps = SharedProps &
  Omit<LinkProps, 'to' | 'className' | 'children'> & {
    to: string
    href?: never
  }

type AnchorButtonProps = SharedProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'className' | 'children'> & {
    href: string
    to?: never
  }

type NativeButtonProps = SharedProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & {
    href?: never
    to?: never
  }

type ButtonProps = LinkButtonProps | AnchorButtonProps | NativeButtonProps

const baseClasses =
  'inline-flex min-h-[44px] items-center justify-center gap-2 border px-5 font-medium tracking-[0.08em] uppercase transition duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 focus-visible:ring-offset-2 w-full sm:w-auto hover:scale-[1.02] active:scale-[0.97] sm:active:scale-[0.98]'

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'border-brand bg-brand text-white hover:bg-brand/90',
  secondary: 'border-slate-900 bg-slate-900 text-white hover:bg-slate-800',
  ghost: 'border-slate-300 bg-white text-slate-900 hover:border-brand hover:text-brand',
}

const sizeClasses: Record<ButtonSize, string> = {
  md: 'min-h-[44px] py-3 text-xs',
  lg: 'min-h-[48px] py-3.5 text-xs',
}

export function Button({
  children,
  className,
  variant = 'primary',
  size = 'md',
  ...props
}: ButtonProps) {
  const classes = cn(baseClasses, variantClasses[variant], sizeClasses[size], className)

  if ('to' in props && props.to) {
    const { to, ...linkProps } = props as LinkButtonProps
    return (
      <Link className={classes} to={to} {...linkProps}>
        {children}
      </Link>
    )
  }

  if ('href' in props && props.href) {
    const { href, ...anchorProps } = props as AnchorButtonProps
    return (
      <a className={classes} href={href} {...anchorProps}>
        {children}
      </a>
    )
  }

  const buttonProps = props as NativeButtonProps
  return (
    <button className={classes} {...buttonProps}>
      {children}
    </button>
  )
}
